import useAppStore from "@hooks/useAppStore";
import i18n, { supportedLanguages } from "@i18n/i18n";
import LanguageIcon from "@mui/icons-material/Language";
import { AppBar, Button, IconButton, Menu, MenuItem, Stack, ThemeProvider, Toolbar, createTheme, styled, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import * as locales from "@mui/material/locale";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

export const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.background.default,
}));

const LoginPage = observer(() => {
  const appStore = useAppStore();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  const defaultTheme = useMemo(() => {
    const muiLocale = supportedLanguages.find((l) => l.locales.includes(appStore.language))?.mui || "enUS";
    // @ts-ignore No index signature with a parameter of type 'string' was found on type 'typeof
    const locale = locales[muiLocale];

    return createTheme(
      {
        palette: {
          mode: "dark",
          background: {
            default: appStore.colorMode === "light" ? "#FFF" : "#000",
          },
        },
        typography: {
          button: {
            textTransform: "none",
          },
        },
        components: {
          MuiTypography: {
            styleOverrides: {
              root: {
                "&.MuiTypography-root": {
                  fontFamily: "ArtifaktElement, sans-serif",
                },
              },
            },
          },
        },
      },
      locale,
    );
  }, [appStore.colorMode, appStore.language]);

  const handleLanguageClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLangaugeChange = (value: any) => {
    console.log("handleLangaugeChange", value);
    handleLanguageMenuClose();
    appStore.setLanguage(value.locale);
    i18n.changeLanguage(value.locale.toLowerCase());
  };

  useEffect(() => {
    if (appStore.userProfile?.userId) {
      navigate(`/projects`);
    }
  }, []);

  const handleLogin = (e: any) => {
    e.stopPropagation();
    setIsLoading(true);
    const redirectUrl = window.location.protocol + "//" + window.location.host;
    const signInUrl = `/api/auth/signin?redirectUrl=${encodeURIComponent(redirectUrl)}&rememberMe=${rememberMe}`;
    window.location.assign(signInUrl);
  };

  return (
    <>
      <ThemeProvider theme={defaultTheme}>
        <AppBar position="static" color="transparent" elevation={0}>
          <StyledToolbar>
            <Typography component="div" sx={{ flexGrow: 1 }}></Typography>
            <IconButton size="large" aria-label="Language" aria-controls="menu-appbar" aria-haspopup="true" onClick={handleLanguageClick} color="inherit">
              <LanguageIcon />
            </IconButton>
            <ThemeProvider theme={theme}>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={open}
                onClose={handleLanguageMenuClose}
                slotProps={{
                  paper: {
                    elevation: 1,
                    sx: {
                      border: 1,
                      borderColor: "divider",
                      overflow: "visible",
                      "& .MuiList-root": {
                        padding: 0,
                      },
                    },
                  },
                }}
              >
                {supportedLanguages.map((lng: any) => (
                  <MenuItem key={lng.locale} onClick={() => handleLangaugeChange(lng)}>
                    {lng.label}
                  </MenuItem>
                ))}
              </Menu>
            </ThemeProvider>
          </StyledToolbar>
        </AppBar>
        <Toolbar /* This adds padding above the content so they are not covered up by the AppBar */ />
      </ThemeProvider>

      <Box sx={{ height: "60%", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}>
        <Stack direction="column" spacing={6} sx={{ mb: 2 }}>
          {i18n.isInitialized && (
            <>
              <Typography variant="h6">{i18n.t("login-page.header")}</Typography>
              <Typography sx={{ maxWidth: 1200, textAlign: "justify" }}>{i18n.t("login-page.body")}</Typography>
              <Typography variant="body2" sx={{ maxWidth: 1200, textAlign: "center", color: "#000000", mt:"0 !important" }}>
                {i18n.t("login-page.terms-intro")}{" "}
                <Link component={RouterLink} to="/terms" underline="always" color="#000000" fontWeight={500}>
                  {i18n.t("login-page.terms-link")}
                </Link>
              </Typography>
              <Box>
                <Button name="login-btn" loading={isLoading} variant="contained" onClick={handleLogin}>
                  {i18n.t("login-page.login-btn")}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </Box>
    </>
  );
});

export default LoginPage;
